import React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

const NotFound = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: `
          radial-gradient(ellipse at 20% 30%, ${theme.palette.primary.dark}20, transparent),
          radial-gradient(ellipse at 80% 70%, ${theme.palette.primary.dark}20, transparent),
          linear-gradient(135deg, ${theme.palette.background.default} 0%, #0A0A0F 100%)
        `,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Animated floating particles */}
      {[...Array(30)].map((_, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 0, x: 0 }}
          animate={{
            opacity: [0, 0.5, 0],
            y: [0, -Math.random() * 200 - 50],
            x: [0, (Math.random() - 0.5) * 100],
          }}
          transition={{
            duration: Math.random() * 4 + 3,
            repeat: Infinity,
            delay: Math.random() * 5,
            ease: "easeOut",
          }}
          style={{
            position: "absolute",
            left: `${Math.random() * 100}%`,
            bottom: `${Math.random() * 100}%`,
            width: `${Math.random() * 4 + 2}px`,
            height: `${Math.random() * 4 + 2}px`,
            background:
              i % 2 === 0
                ? theme.palette.primary.main
                : theme.palette.primary.main,
            borderRadius: "50%",
            filter: "blur(2px)",
            pointerEvents: "none",
          }}
        />
      ))}

      {/* Main Content */}
      <Box
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, type: "spring" }}
        sx={{
          textAlign: "center",
          zIndex: 2,
          maxWidth: "700px",
          mx: "auto",
          px: { xs: 3, md: 5 },
          py: { xs: 5, md: 7 },
        }}
      >
        {/* 404 Number with glitch effect */}
        <Box sx={{ position: "relative", mb: 2 }}>
          <Typography
            sx={{
              fontSize: { xs: "100px", md: "160px" },
              fontWeight: 900,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.main})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              letterSpacing: "15px",
              textShadow: `0 0 50px ${theme.palette.primary.main}40`,
              position: "relative",
              "&::before, &::after": {
                content: '"404"',
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                background: "inherit",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
              },
              "&::before": {
                left: "2px",
                textShadow: "none",
                transform: "skew(15deg)",
                opacity: 0.5,
                zIndex: -1,
              },
              "&::after": {
                left: "-2px",
                textShadow: "none",
                transform: "skew(-15deg)",
                opacity: 0.5,
                zIndex: -1,
              },
            }}
          >
            404
          </Typography>
        </Box>

        {/* Main message */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <Typography
            variant="h3"
            sx={{
              fontWeight: 800,
              mb: 10,
              fontSize: { xs: "1.5rem", md: "2rem" },
              background: `linear-gradient(135deg, ${theme.palette.text.primary}, ${theme.palette.primary.light})`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
            }}
          >
            صفحه ای که دنبالشی پیدا نشد !
          </Typography>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              gap: 2.5,
              justifyContent: "center",
              flexWrap: "wrap",
              mb: 5,
            }}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="contained"
                size="large"
                onClick={() => navigate("/")}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.main})`,
                  px: 5,
                  py: 1.5,
                  borderRadius: "50px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  boxShadow: `0 4px 20px ${theme.palette.primary.main}60`,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                }}
              >
                برو به صفحه اصلی
              </Button>
            </motion.div>

            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate(-1)}
                sx={{
                  borderColor: theme.palette.primary.main,
                  color: theme.palette.primary.main,
                  px: 5,
                  py: 1.5,
                  borderRadius: "50px",
                  fontSize: "1rem",
                  fontWeight: 700,
                  textTransform: "none",
                  "&:hover": {
                    borderColor: theme.palette.primary.light,
                    background: `${theme.palette.primary.main}15`,
                  },
                }}
              >
                برگرد عقب
              </Button>
            </motion.div>
          </Box>

          {/* Quick links row */}
          <Box
            sx={{
              display: "flex",
              gap: 3,
              justifyContent: "center",
              flexWrap: "wrap",
              pt: 3,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            {[
              { label: "درباره ما", path: "/about-us", icon: "" },
              { label: "مربیان", path: "/coaches", icon: "" },
              { label: "باشگاه ها", path: "/gyms", icon: "" },
            ].map((link, idx) => (
              <motion.div
                key={idx}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <Button
                  onClick={() => navigate(link.path)}
                  sx={{
                    color: theme.palette.text.primary,
                    fontSize: "0.9rem",
                    textTransform: "none",
                    "&:hover": {
                      color: theme.palette.primary.main,
                      background: "transparent",
                    },
                  }}
                >
                  {link.icon} {link.label}
                </Button>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default NotFound;
